import {useForm} from 'react-hook-form';
import Modal from './common/Modal';
import {useMutation} from '@tanstack/react-query';
import {createInvigilator} from '../api/auth';
import toast from 'react-hot-toast';
import Button from './ui/Button';
import {SelectFieldHookForm} from './ui/SelectField';
import {
  FaUserPlus,
  FaEnvelope,
  FaUsers,
  FaCheck,
  FaExclamationTriangle,
  FaInfoCircle,
} from 'react-icons/fa';

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const CreateInvigilatorModal = ({isOpen, onClose, assessmentId}) => {
  const {handleSubmit, register, control, reset, setValue, getValues, watch} =
    useForm({
      mode: 'all',
    });

  const emailIds = watch('emailIds');

  const createUserMutation = useMutation(createInvigilator, {
    onSuccess: () => {
      toast.success('Invigilators created successfully');
      onClose();
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Something went wrong');
    },
  });

  const onSubmit = (data) => {
    // Validate all email addresses
    const invalidEmails = data.emailIds.filter(
      (email) => !emailRegex.test(email.value),
    );
    if (invalidEmails.length > 0) {
      toast.error('Please enter valid email addresses');
      return;
    }
    const emailIds = data.emailIds.map((d) => d.value);

    const payload = {
      emailIds,
      role: 'invigilator',
      assessmentId: assessmentId,
    };
    createUserMutation.mutate(payload);
  };

  const validateEmail = (inputValue) => {
    if (!inputValue) return true;
    return emailRegex.test(inputValue);
  };

  const addEmailsToForm = (emails) => {
    const currentValues = getValues('emailIds') || [];
    const newEmails = emails
      .filter((email) => email.trim() !== '')
      .filter((email) => validateEmail(email.trim()))
      .filter(
        (email) =>
          !currentValues.some((existing) => existing.value === email.trim()),
      )
      .map((email) => ({value: email.trim(), label: email.trim()}));

    if (newEmails.length > 0) {
      setValue('emailIds', [...currentValues, ...newEmails]);
    }

    return newEmails.length;
  };

  const handleInputChange = (inputValue, {action}) => {
    // Check if the input contains multiple emails (space, comma, semicolon separated)
    if (action === 'input-change' && inputValue && /[,;\s]+/.test(inputValue)) {
      const emailSeparators = /[,;\s\n\r\t]+/;
      const potentialEmails = inputValue.split(emailSeparators);

      if (potentialEmails.length > 1) {
        const validEmails = potentialEmails.filter(
          (email) => email.trim() !== '' && validateEmail(email.trim()),
        );

        const invalidEmails = potentialEmails.filter(
          (email) => email.trim() !== '' && !validateEmail(email.trim()),
        );

        const addedCount = addEmailsToForm(validEmails);

        if (addedCount > 0) {
          toast.success(`Added ${addedCount} valid email(s)`);
        }

        if (invalidEmails.length > 0) {
          toast.error(`${invalidEmails.length} invalid email(s) were skipped`);
        }

        // Return empty string to clear the input
        return '';
      }
    }

    return inputValue;
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const inputValue = event.target.value.trim();
      if (!validateEmail(inputValue)) {
        event.preventDefault();
        toast.error('Please enter a valid email address');
      }
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');

    // Parse emails from pasted text using common separators
    const emailSeparators = /[,;\s\n\r\t]+/;
    const potentialEmails = pastedText.split(emailSeparators);

    const validEmails = potentialEmails.filter(
      (email) => email.trim() !== '' && validateEmail(email.trim()),
    );

    const invalidEmails = potentialEmails.filter(
      (email) => email.trim() !== '' && !validateEmail(email.trim()),
    );

    const addedCount = addEmailsToForm(validEmails);

    if (addedCount > 0) {
      toast.success(`Added ${addedCount} valid email(s)`);
    }

    if (invalidEmails.length > 0) {
      toast.error(`${invalidEmails.length} invalid email(s) were skipped`);
    }

    // Clear the input after paste
    event.target.value = '';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      width="md"
      containerClassName="absolute-center bg-gray-50 max-w-[40%] p-0 z-30 overflow-auto max-h-[85vh] rounded-xl shadow-lg border border-gray-200"
    >
      <style>
        {`
          .close-btn {
            display: none !important;
          }
        `}
      </style>

      <div className="p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-main rounded-full mb-3 shadow-lg">
            <FaUserPlus className="text-xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">
            Create Invigilators
          </h2>
          <p className="text-gray-600">
            Add invigilators to oversee this assessment
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Email Input Section - More Prominent */}
          <div className="bg-white rounded-lg p-5 shadow-md border-2 border-primary-main/20 relative">
            {/* Highlight Border */}
            <div className="absolute inset-0 rounded-lg border-2 border-primary-main/10 pointer-events-none"></div>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-main rounded-full shadow-md">
                <FaEnvelope className="text-white text-lg" />
              </div>
              <div>
                <label className="text-xl font-bold text-gray-800 block">
                  Email Addresses
                </label>
                <p className="text-sm text-gray-600">
                  Enter email addresses for new invigilators
                </p>
              </div>
            </div>

            <SelectFieldHookForm
              control={control}
              name="emailIds"
              placeholder="Enter email addresses (e.g., john@example.com, jane@example.com)"
              rules={{
                required: 'At least one email address is required',
                validate: validateEmail,
              }}
              multiple
              creatable
              isClearable
              className="w-full"
              isValidNewOption={validateEmail}
              formatCreateLabel={(inputValue) => (
                <div className="flex items-center gap-2">
                  <FaCheck className="text-primary-main" />
                  <span>Add "{inputValue}"</span>
                </div>
              )}
              options={[]}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onInputChange={handleInputChange}
              hideDropdown={true}
            />

            {/* Email Counter */}
            {emailIds && emailIds.length > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <div className="flex items-center gap-2 bg-primary-main/10 text-primary-main px-3 py-2 rounded-lg border border-primary-main/20">
                  <FaUsers className="text-primary-main" />
                  <span className="font-semibold text-sm">
                    {emailIds.length} email{emailIds.length !== 1 ? 's' : ''}{' '}
                    added
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Info Section - Less Prominent */}
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <FaInfoCircle className="text-gray-500 mt-0.5 flex-shrink-0 text-sm" />
              <div>
                <h4 className="text-gray-700 font-medium text-sm mb-1">
                  How to add emails:
                </h4>
                <ul className="text-gray-600 text-xs space-y-0.5">
                  <li>• Type email addresses and press Enter</li>
                  <li>
                    • Paste multiple emails separated by commas, spaces, or
                    newlines
                  </li>
                  <li>• Invalid emails will be automatically filtered out</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
            >
              Cancel
            </button>

            <Button
              type="submit"
              disabled={
                createUserMutation.isLoading ||
                !emailIds ||
                emailIds.length === 0
              }
              className="px-6 py-2 bg-primary-main hover:bg-primary-main/90 text-white rounded-lg font-semibold shadow-md transform transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center justify-center gap-2 whitespace-nowrap min-w-fit"
            >
              {createUserMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent flex-shrink-0" />
                  <span className="flex-shrink-0">Creating...</span>
                </>
              ) : (
                <span className="flex gap-2 items-center">
                  <FaUserPlus className="flex-shrink-0 w-4 h-4" />
                  <span className="flex-shrink-0">Create Invigilators</span>
                </span>
              )}
            </Button>
          </div>

          {/* Loading State Overlay */}
          {createUserMutation.isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-xl">
              <div className="flex items-center gap-2 bg-white px-4 py-3 rounded-lg shadow-lg">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-main border-t-transparent" />
                <span className="font-medium text-gray-700">
                  Creating invigilators...
                </span>
              </div>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
};

export default CreateInvigilatorModal;
